import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SetupOverviewItem } from '../../../services/setupOverview/setupOverviewService';
import { createCostPriceCategory, fetchCostPriceCategoriesByRestaurant, type CostPriceCategory } from '../../../services/costPrice/costPriceCategoriesService';
import { updateCostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { ALL_RESTAURANTS_ID } from '../../../stores/appUserDefaultStore';
import { useScopedRestaurant, useRestaurantScopeCurrent } from '../../../hooks/restaurantViewScope';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { findOrCreateChoiceCostPrice } from '../../../utils/costPriceUtils';
import { resolveCostTargets } from '../../../utils/costTargets';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import CategoryAssignmentModal from '../../costPrice/CategoryAssignmentModal';
import CostPriceDetailModal from '../costPrice/new/CostPrice/CostPriceDetailModal';
import { ensureSetupItemAnalysis } from './setupOverviewEditing';
import { loadSetupEditorData } from './setupOverviewEditorData';
import { useAuth } from '../../../services/authentication/authContext';
import { winningDishAnalyses } from '../../../utils/reports/utilizationReport';

export function useSetupOverviewEditor(onChanged: () => void) {
  const { t } = useTranslation();
  const { backendUser } = useAuth();
  // The access poll replaces backendUser every 10s; preload per user, not per profile object.
  const backendUserRef = useRef(backendUser);
  backendUserRef.current = backendUser;
  const userId = backendUser?.id;
  const selectedId = useScopedRestaurant()?.id;
  const scopeCurrent = useRestaurantScopeCurrent();
  const reportSettings = useRestaurantStore(state => state.reportSettings);
  const restaurantDataLoaded = useRestaurantStore(state => state.isDataLoaded);
  const [preload, setPreload] = useState<{ id?: string; ready: boolean; error: boolean }>({ ready: false, error: false });
  const [preloadAttempt, setPreloadAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const operation = useRef(0);
  const [categoryEditor, setCategoryEditor] = useState<{
    item: SetupOverviewItem; categories: CostPriceCategory[]; selectionId: string | undefined;
  } | null>(null);
  const [detail, setDetail] = useState<{ restaurantId: string; id: string; parentId?: string } | null>(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    setCategoryEditor(editor => editor?.selectionId === selectedId ? editor : null);
    setDetail(editor => editor?.restaurantId === selectedId ? editor : null);
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId || selectedId === ALL_RESTAURANTS_ID || !restaurantDataLoaded) return;
    let active = true;
    const current = () => active && scopeCurrent(selectedId);
    setPreload({ id: selectedId, ready: false, error: false });
    loadSetupEditorData(selectedId, backendUserRef.current, current).then(() => {
      if (current()) setPreload({ id: selectedId, ready: true, error: false });
    }).catch(error => {
      if (!current()) return;
      console.error('Setup pricing preload failed', error);
      setPreload({ id: selectedId, ready: false, error: true });
    });
    return () => { active = false; };
  }, [selectedId, restaurantDataLoaded, userId, preloadAttempt, scopeCurrent]);
  const dataReady = preload.id === selectedId && preload.ready;

  const begin = () => {
    if (busyRef.current) return null;
    busyRef.current = true; setBusy(true);
    return ++operation.current;
  };
  const finish = () => { busyRef.current = false; if (mounted.current) setBusy(false); };
  const isCurrent = (token: number, selection: string | undefined) => mounted.current && operation.current === token &&
    scopeCurrent(selection);
  const failed = (error: unknown) => { console.error('Setup overview edit failed', error); window.alert(t('setupOverview.editFailed')); };

  const openCategory = async (item: SetupOverviewItem) => {
    if (!dataReady || item.restaurantId !== selectedId) return;
    const token = begin(); if (token === null) return;
    const selectionId = selectedId;
    try {
      const categories = useCostPriceCategoriesStore.getState().categories;
      if (isCurrent(token, selectionId)) setCategoryEditor({ item, categories, selectionId });
    } catch (error) { if (isCurrent(token, selectionId)) failed(error); }
    finally { finish(); }
  };

  const assignCategory = async (categoryId?: string, newName?: string) => {
    if (!categoryEditor) return;
    const token = begin(); if (token === null) return;
    const { item, selectionId } = categoryEditor;
    const current = () => isCurrent(token, selectionId);
    try {
      if (!current()) return;
      if (newName) {
        const created = await createCostPriceCategory({ restaurantId: item.restaurantId, name: newName.trim(), scope: 'revenue' });
        categoryId = created.id;
        if (!current()) return;
        setCategoryEditor(editor => editor ? { ...editor, categories: [...editor.categories, created] } : editor);
      }
      if (!categoryId) return;
      const analysis = await ensureSetupItemAnalysis(item, categoryId, current);
      if (!current()) return;
      const updated = await updateCostPriceAnalysis({ id: analysis.id, categoryId });
      if (!mounted.current) return;
      const store = useCostPriceStore.getState();
      if (store.currentRestaurantId === item.restaurantId || store.currentRestaurantId === ALL_RESTAURANTS_ID) store.upsertOne(updated);
      if (current()) setCategoryEditor(null);
      onChanged();
      // Keep the regular revenue/cost-price pages' cached categories in sync as well.
      if (useCostPriceCategoriesStore.getState().currentRestaurantId === item.restaurantId) {
        await useCostPriceCategoriesStore.getState().load(item.restaurantId, { force: true });
      }
    } catch (error) { if (current()) failed(error); }
    finally { finish(); }
  };

  const openCost = async (item: SetupOverviewItem) => {
    if (!dataReady || item.restaurantId !== selectedId) return;
    const token = begin(); if (token === null) return;
    const selectionId = selectedId;
    const current = () => isCurrent(token, selectionId);
    try {
      const store = useCostPriceStore.getState();
      if (store.currentRestaurantId !== item.restaurantId) throw new Error('Restaurant data unavailable');
      let analysis = winningDishAnalyses(store.costPriceList).get(item.itemName);
      if (!analysis) {
        const categories = useCostPriceCategoriesStore.getState().categories;
        const defaultCategory = categories.find(category => category.isDefault && (category.scope ?? 'revenue') === 'revenue');
        analysis = await ensureSetupItemAnalysis(item, defaultCategory?.id, current);
        if (!current()) return;
        store.upsertOne(analysis);
        onChanged();
      }
      if (!current()) return;
      setDetail({ restaurantId: item.restaurantId, id: analysis.id });
    } catch (error) { if (current()) failed(error); }
    finally { finish(); }
  };

  // Ingredient/category writes update the existing store. Refresh the server's counts after each save.
  const detailRestaurantId = detail?.restaurantId;
  useEffect(() => {
    if (!detailRestaurantId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = useCostPriceStore.subscribe((state, previous) => {
      if (state.currentRestaurantId !== detailRestaurantId || state.costPriceList === previous.costPriceList) return;
      clearTimeout(timer);
      timer = setTimeout(onChanged, 200);
    });
    return () => { unsubscribe(); clearTimeout(timer); };
  }, [detailRestaurantId, onChanged]);

  const closeDetail = useCallback(() => {
    operation.current++;
    setDetail(previous => previous?.parentId ? { restaurantId: previous.restaurantId, id: previous.parentId } : null);
    onChanged();
  }, [onChanged]);
  const editChoice = async (choiceName: string, parentCategoryId: string | undefined) => {
    if (!detail) return;
    const token = begin(); if (token === null) return;
    const current = () => isCurrent(token, detail.restaurantId);
    try {
      if (!current()) return;
      const choice = await findOrCreateChoiceCostPrice(detail.restaurantId, choiceName, parentCategoryId, useCostPriceStore.getState().costPriceList);
      if (!current()) return;
      useCostPriceStore.getState().upsertOne(choice);
      if (current()) setDetail({ restaurantId: detail.restaurantId, id: choice.id, parentId: detail.id });
    } catch (error) { if (current()) failed(error); }
    finally { finish(); }
  };

  const settings = detail ? reportSettings[detail.restaurantId] : undefined;
  return { busy, dataReady, dataError: preload.id === selectedId && preload.error,
    retryPreload: () => setPreloadAttempt(value => value + 1), openCategory, openCost, overlays: <>
    {categoryEditor && categoryEditor.selectionId === selectedId && <CategoryAssignmentModal
      itemName={categoryEditor.item.itemName}
      displayName={`${categoryEditor.item.itemName} — ${categoryEditor.item.restaurantName}`}
      currentCategoryId={categoryEditor.item.categoryId ?? categoryEditor.categories.find(category => category.isDefault && category.scope === 'revenue')?.id ?? ''}
      categories={categoryEditor.categories.filter(category => (category.scope ?? 'revenue') === 'revenue')}
      onSelectCategory={id => assignCategory(id)} onCreateAndAssignCategory={name => assignCategory(undefined, name)}
      onCategoryUpdated={async () => {
        const editor = categoryEditor;
        const categories = await fetchCostPriceCategoriesByRestaurant(editor.item.restaurantId);
        if (mounted.current) setCategoryEditor(previous => previous === editor ? { ...previous, categories } : previous);
        onChanged();
      }}
      onClose={() => { operation.current++; setCategoryEditor(null); }} />}
    {detail && detail.restaurantId === selectedId && <CostPriceDetailModal key={detail.id} costPriceId={detail.id}
      onClose={closeDetail} onBack={detail.parentId ? closeDetail : undefined} byoParentId={detail.parentId}
      onEditChoice={editChoice} costTargets={resolveCostTargets(settings?.costTargets)}
      defaultWastePercentage={resolveWastePercentage(settings?.wastePercentage)} />}
  </> };
}
